/* eslint-disable @typescript-eslint/no-explicit-any -- U2000Input* form handlers */
import React from 'react';
import type { TFunction } from 'i18next';
import U2000InputCalendarCustom from '../../../u2000Core/components/formControls/input/U2000InputCalendarCustom';
import U2000InputText from '../../../u2000Core/components/formControls/input/U2000InputText';
import type { U2000FormStateHandlers } from '../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { U2000FormStateEnumState } from '../../../u2000Core/reducer/formReducer/U2000FormState';
import { NS } from '../../U3303';
import { createEmptyHolidayDayRow, type U3303HolidayYearFormRow } from '../../U3303Service';

interface U3303HolidayDaysSectionProps {
  formStateHandlers: U2000FormStateHandlers<U3303HolidayYearFormRow>;
  t: TFunction;
}

const U3303HolidayDaysSection: React.FC<U3303HolidayDaysSectionProps> = ({ formStateHandlers, t }) => {
  const data = formStateHandlers.getData();
  const rows = data?.days ?? [];
  const formState = formStateHandlers.getFormState();
  const canEditStructure = formState === U2000FormStateEnumState.EDIT;

  const addDay = () => {
    const current = formStateHandlers.getData();
    if (!current) return;
    formStateHandlers.setData({
      ...current,
      days: [...(current.days ?? []), createEmptyHolidayDayRow()],
    });
  };

  const removeDayAt = (index: number) => {
    const current = formStateHandlers.getData();
    if (!current) return;
    formStateHandlers.setData({
      ...current,
      days: (current.days ?? []).filter((_, i) => i !== index),
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-gray-900">{t('lblSectionDays', { ns: NS })}</h3>
        {canEditStructure && (
          <button
            type="button"
            className="rounded-md bg-slate-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
            onClick={addDay}
          >
            {t('btnAddDay', { ns: NS })}
          </button>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('lblHolidayDate', { ns: NS })}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('lblLabel', { ns: NS })}</th>
              {canEditStructure && <th className="w-28 px-3 py-2 text-center font-medium text-gray-700" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {rows.length === 0 && (
              <tr>
                <td colSpan={canEditStructure ? 3 : 2} className="px-3 py-6 text-center text-sm text-gray-500">
                  {t('msgNoHolidayDays', { ns: NS })}
                </td>
              </tr>
            )}
            {rows.map((day, index) => (
              <tr key={day.id || `day-${index}`}>
                <td className="min-w-[200px] px-3 py-2 align-middle">
                  <div className="flex min-w-0 flex-col gap-1">
                    <U2000InputCalendarCustom
                      popupZIndex={9999}
                      formStateHandlers={formStateHandlers as any}
                      fieldName={`days.${index}.holidayDate`}
                      label=""
                      required
                      dateFormat="yyyy-MM-dd"
                      onAfterSelect={() => {
                        const el = document.getElementById(`days.${index}.label`) as HTMLInputElement | null;
                        el?.focus({ preventScroll: true });
                      }}
                    />
                  </div>
                </td>
                <td className="min-w-[220px] px-3 py-2 align-middle">
                  <U2000InputText
                    hideLabel
                    formStateHandlers={formStateHandlers as any}
                    fieldName={`days.${index}.label`}
                    label={t('lblLabel', { ns: NS })}
                    required
                    selectOnFocus={false}
                  />
                </td>
                {canEditStructure && (
                  <td className="px-3 py-2 text-center align-middle">
                    <button
                      type="button"
                      className="text-sm font-medium text-red-600 hover:text-red-800"
                      onClick={() => removeDayAt(index)}
                    >
                      {t('btnRemoveDay', { ns: NS })}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default U3303HolidayDaysSection;
